import React, { memo, useCallback } from 'react';
import { Paper, Typography, Box, alpha } from '@mui/material';
import { Note } from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';

const NoteNode = memo(({ data, selected, id }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  // Get theme-aware colors for note (sticky note appearance)
  const noteColor = data.color || theme.palette.warning.main;
  const noteTextColor = isDark ? theme.palette.grey[900] : theme.palette.grey[700];
  const borderColor = selected ? theme.palette.secondary.main : alpha(theme.palette.warning.dark, 0.6);
  const hoverColor = alpha(noteColor, 0.9);

  const handleDoubleClick = useCallback((event) => {
    event.stopPropagation();
    const editEvent = new CustomEvent('editNode', { detail: { nodeId: id } });
    window.dispatchEvent(editEvent);
  }, [id]);

  const handleContextMenu = useCallback((event) => {
    event.preventDefault();
    event.stopPropagation();
    const contextEvent = new CustomEvent('nodeContextMenu', {
      detail: {
        nodeId: id,
        x: event.clientX,
        y: event.clientY
      }
    });
    window.dispatchEvent(contextEvent);
  }, [id]);

  return (
    <Paper
      elevation={selected ? 6 : 2}
      onDoubleClick={handleDoubleClick}
      onContextMenu={handleContextMenu}
      sx={{
        padding: 2.5,
        borderRadius: 1,
        minWidth: 160,
        minHeight: 80,
        backgroundColor: noteColor,
        border: selected ? `2px solid ${borderColor}` : `1px dashed ${alpha(theme.palette.warning.dark, 0.8)}`,
        cursor: 'move',
        position: 'relative',
        transition: theme.transitions.create([
          'transform', 'box-shadow', 'background-color'
        ], {
          duration: theme.transitions.duration.shorter,
        }),
        '&:hover': {
          transform: 'translateY(-3px) rotate(1deg)',
          boxShadow: theme.shadows[6],
          backgroundColor: hoverColor,
        },
        '&:focus-within': {
          outline: `2px solid ${theme.palette.secondary.main}`,
          outlineOffset: 2,
        },
        '&[aria-selected="true"]': {
          boxShadow: `0 0 0 3px ${alpha(theme.palette.secondary.main, 0.3)}`,
        },
        // Sticky note corner fold effect
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          right: 0,
          width: 20,
          height: 20,
          backgroundColor: isDark ? alpha(theme.palette.common.white, 0.1) : alpha(theme.palette.common.white, 0.8),
          borderLeft: `1px dashed ${alpha(theme.palette.warning.dark, 0.6)}`,
          borderBottom: `1px dashed ${alpha(theme.palette.warning.dark, 0.6)}`,
          clipPath: 'polygon(0 0, 100% 0, 100% 100%)',
          transition: theme.transitions.create('background-color'),
        },
        // Shadow effect for sticky note
        '&::after': {
          content: '""',
          position: 'absolute',
          top: 2,
          left: 2,
          right: 2,
          bottom: 2,
          backgroundColor: alpha(theme.palette.common.black, 0.1),
          borderRadius: 'inherit',
          zIndex: -1,
          opacity: selected ? 0.3 : 0.1,
          transition: theme.transitions.create('opacity'),
        },
      }}
      role="button"
      tabIndex={0}
      aria-label={`Note: ${data.label || 'Note'}`}
      aria-selected={selected}
    >
      <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 1 }}>
        <Note
          sx={{
            mr: 1,
            fontSize: 20,
            color: noteTextColor,
            filter: isDark ? 'brightness(0.8)' : 'none',
          }}
        />
        <Typography
          variant="subtitle2"
          fontWeight="600"
          sx={{
            color: noteTextColor,
            fontSize: '0.9rem',
            lineHeight: 1.2,
          }}
        >
          {data.label || 'Note'}
        </Typography>
      </Box>

      {data.description && (
        <Typography
          variant="body2"
          sx={{
            display: 'block',
            color: alpha(noteTextColor, 0.9),
            fontSize: '0.8rem',
            lineHeight: 1.4,
            fontStyle: 'italic',
            mt: 1,
          }}
        >
          {data.description}
        </Typography>
      )}

      {/* Pin indicator */}
      <Box
        sx={{
          position: 'absolute',
          top: -4,
          left: '50%',
          transform: 'translateX(-50%)',
          width: 8,
          height: 8,
          borderRadius: '50%',
          backgroundColor: theme.palette.error.main,
          border: `2px solid ${alpha(theme.palette.common.white, 0.8)}`,
          boxShadow: `0 2px 4px ${alpha(theme.palette.common.black, 0.2)}`,
        }}
      />
    </Paper>
  );
});

NoteNode.displayName = 'NoteNode';

export default NoteNode;